import { useCallback, useEffect, useRef, useState } from 'react';

interface UseActiveSectionIndexProps {
  isEnabled: boolean;
}

export function useActiveSectionIndex({
  isEnabled,
}: UseActiveSectionIndexProps) {
  const [activeSectionIndex, setActiveSectionIndex] = useState(
    isEnabled ? 0 : 1
  );
  const markersRef = useRef<(HTMLDivElement | null)[]>([]);

  useEffect(() => {
    if (!isEnabled) return;

    const markerElements = markersRef.current.filter(
      (x): x is HTMLDivElement => !!x
    );

    const intersectionObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const markerIndex = markerElements.findIndex(
              (x) => x === entry.target
            );

            if (markerIndex < 0) return;

            setActiveSectionIndex(markerIndex);
          }
        });
      },
      { threshold: 0.5 }
    );

    markerElements.forEach((markerElement) =>
      intersectionObserver.observe(markerElement)
    );

    return () => intersectionObserver.disconnect();
  }, [isEnabled]);

  // Set `activeSectionIndex` to 1 by default when using the simplified layout.
  // This will keep the first pillar's accordion open by default. When using the
  // sticky layout, no pillar is selected by default.
  useEffect(() => {
    if (isEnabled) {
      setActiveSectionIndex(0);
    } else {
      setActiveSectionIndex(1);
    }
  }, [isEnabled]);

  const jumpToSection = useCallback(
    (sectionIndex: number) => {
      if (!isEnabled) return;

      markersRef.current[sectionIndex]?.scrollIntoView();
    },
    [isEnabled]
  );

  return {
    activeSectionIndex,
    markersRef,
    jumpToSection,
    setActiveSectionIndex,
  };
}
